import { useEffect, useMemo } from 'react'

import curriculumSanity from '~/shared/sanity/curriculum'
import { slugify } from '~/shared/sanity/utils/curriculum.utils'
import { CurriculumCountryCodes } from '~/curriculum/constants/curriculum.const'
import { mergeProgressIntoGradeData } from '~/curriculum/utils/mergeProgressIntoGradeData'
import { countMustKnowBlocks, mergeTopicMustKnowProgress } from '~/curriculum/utils/mustKnowCompletion.utils'
import { isMappingQuizApplicable } from '~/curriculum/utils/validateCountryCodeGrade'
import {
  useGetCurriculumProgressSummaryQuery,
  useGetCurriculumTopicProgressSummaryQuery,
} from '~/curriculum/apis/curriculum-progress.api'
import { useGetMappingQuizPrerequisiteTasksQuery } from '~/curriculum/apis/mapping-quiz.api'
import { useGetCurrentGradeQuery } from '~/curriculum/apis/curriculum.api'
import {
  selectGradeOverviewFromSanity,
  setGradeOverviewFromSanityActionCreator,
} from '~/curriculum/slice/curriculum.slice'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { MustKnowsProgressCardProps } from '~/statistics-v2/types/statistics.types'

interface UseMasteryTrackV2SubtopicStatsArgs {
  enabled: boolean
  correctedCountryCode: string | undefined
  gradeData: ReturnType<typeof useGetCurrentGradeQuery>['data']
}

interface UseMasteryTrackV2SubtopicStatsResult {
  topicProgressPercentage: number | undefined
  mustKnowsProgressCardProps: MustKnowsProgressCardProps
  hasSubtopicStats: boolean
}

const buildSanityGradeId = (countryCode: string, gradeLevel: number): string => {
  const sanityCountry =
    countryCode === CurriculumCountryCodes.DEFAULT || countryCode === CurriculumCountryCodes.UKRAINIAN
      ? CurriculumCountryCodes.USA
      : countryCode
  return `${sanityCountry}-grade-${gradeLevel}`
}

/**
 * Lightweight replacement for the `useGetUserStats`-based computation previously used to populate
 * the v2 MasteryTrackPanel pie chart and must-knows badge. Mirrors the data flow used by
 * `TopicDetailsPanel` / `SubTopicSelectionCard` so the dashboard shows the same per-subtopic numbers.
 * Revert this entire hook once `/v1/progress/get-user-stats` performance is acceptable.
 *
 * Must-know counting matches hom-api's own definition (`mustknow.helper.ts:getMustKnowProgress`):
 * a must-know is a *block* (a titled group of levels), and it counts as complete only when every
 * level in it is complete. Totals therefore come from the mapping-quiz task row and completion from
 * the nested `mustKnows[levelSlug].levels[levelId].isComplete` in the progress summary.
 */
const useMasteryTrackV2SubtopicStats = ({
  enabled,
  correctedCountryCode,
  gradeData,
}: UseMasteryTrackV2SubtopicStatsArgs): UseMasteryTrackV2SubtopicStatsResult => {
  const dispatch = useAppDispatch()
  const sanityGradeData = useAppSelector(selectGradeOverviewFromSanity)

  const expectedSanityGradeId =
    correctedCountryCode && gradeData?.grade ? buildSanityGradeId(correctedCountryCode, gradeData.grade) : null

  useEffect(() => {
    if (!enabled || !expectedSanityGradeId) return
    if (sanityGradeData?.gradeId === expectedSanityGradeId) return
    curriculumSanity.getGradeContentFromSanity(expectedSanityGradeId).then((response) => {
      dispatch(setGradeOverviewFromSanityActionCreator(response))
    })
  }, [enabled, expectedSanityGradeId, sanityGradeData?.gradeId, dispatch])

  const { data: progressSummary, isLoading: isProgressSummaryLoading } = useGetCurriculumProgressSummaryQuery(
    { country: correctedCountryCode ?? '', grade_id: sanityGradeData?.gradeId ?? '' },
    { skip: !enabled || !correctedCountryCode || !sanityGradeData?.gradeId },
  )

  const mergedGradeData = useMemo(
    () => mergeProgressIntoGradeData(sanityGradeData, progressSummary),
    [sanityGradeData, progressSummary],
  )

  const currentTopic = useMemo(() => {
    if (!mergedGradeData || gradeData?.subjectOrder == null) return undefined
    return mergedGradeData.subDivisions
      .flatMap((sd) => sd.topics)
      .find((topic) => topic.subjectOrder === gradeData.subjectOrder)
  }, [mergedGradeData, gradeData?.subjectOrder])

  const currentSubtopic = useMemo(() => {
    if (!currentTopic || gradeData?.subTopicOrder == null) return undefined
    return currentTopic.competencyGoals.find((cg) => cg.level === gradeData.subTopicOrder)
  }, [currentTopic, gradeData?.subTopicOrder])

  const isSanityGradeReady = sanityGradeData?.gradeId === expectedSanityGradeId
  const isStatsLoading = enabled && (!isSanityGradeReady || isProgressSummaryLoading)

  const hasSubtopicStats = Boolean(currentSubtopic && currentSubtopic.levelCount > 0)

  const topicProgressPercentage: number | undefined = isStatsLoading
    ? undefined
    : hasSubtopicStats
    ? Math.ceil(100 * ((currentSubtopic!.completeCount ?? 0) / currentSubtopic!.levelCount))
    : 0

  const { data: topicProgressSummary } = useGetCurriculumTopicProgressSummaryQuery(
    { topicId: currentTopic?._id ?? '' },
    { skip: !enabled || !currentTopic?._id },
  )

  // The must-know *definitions* (how many there are) live in the user's mapping-quiz row
  // (`curriculum_mapping_quiz_tasks`), not in the progress summary — `mustKnows` in the progress
  // payload only ever contains levels the user already has activity on, and its per-entry
  // `isComplete` is hard-coded `false` by the API and never updated. Counting that map is why the
  // badge showed a level-ish total with `completed` stuck at 0.
  const isMustKnowsApplicable =
    enabled &&
    !!correctedCountryCode &&
    gradeData?.grade != null &&
    isMappingQuizApplicable({ countryCode: correctedCountryCode, grade: String(gradeData.grade) })

  // Unscoped (whole-topic) request: served straight from the Postgres row with no Sanity/Redis
  // resolution, and leaves levels lightweight so completion comes from the aggregate progress flag
  // — the same request `TopicDetailsPanel`/`MustKnowCard` make.
  const { data: mustKnowTasks } = useGetMappingQuizPrerequisiteTasksQuery(
    {
      countryCode: correctedCountryCode ?? '',
      gradeLevel: String(gradeData?.grade ?? ''),
      subjectOrder: gradeData?.subjectOrder ?? 0,
      version: 'v2',
    },
    { skip: !isMustKnowsApplicable || gradeData?.subjectOrder == null },
  )

  // `tasks` is keyed by the slugified competency-goal description (hom-api `slugifyMustKnows`),
  // NOT by the Sanity subtopic `_id`.
  const subTopicSlug = currentSubtopic?.description ? slugify(currentSubtopic.description) : ''

  const subtopicMustKnowBlocks = subTopicSlug ? mustKnowTasks?.tasks?.[subTopicSlug] : undefined

  // Merged across the topic's subtopics: a must-know level's progress is recorded under whichever
  // subtopic the user worked it from, which is not necessarily the one it is listed under.
  const topicMustKnowProgress = useMemo(
    () => mergeTopicMustKnowProgress(topicProgressSummary, currentTopic?._id),
    [topicProgressSummary, currentTopic?._id],
  )

  const mustKnowsProgressCardProps = useMemo<MustKnowsProgressCardProps>(
    () => countMustKnowBlocks(subtopicMustKnowBlocks, topicMustKnowProgress),
    [subtopicMustKnowBlocks, topicMustKnowProgress],
  )

  return {
    topicProgressPercentage,
    mustKnowsProgressCardProps,
    hasSubtopicStats,
  }
}

export default useMasteryTrackV2SubtopicStats
